/**
 * SSE (Server-Sent Events) Service for FNOL Notifications
 * Maintains persistent connection to backend and handles real-time notifications
 * Works even when tab is in background or inactive
 */

class SSEService {
  constructor() {
    this.eventSource = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5; // Reduced from 10
    this.reconnectDelay = 5000; // Start with 5 seconds instead of 3
    this.maxReconnectDelay = 30000; // Max 30 seconds
    this.listeners = {
      newFNOL: [],
      heartbeat: [],
      error: [],
      connected: [],
      disconnected: []
    };
    this.isConnected = false;
    this.reconnectTimeout = null;
    this.lastHeartbeat = null;
    this.heartbeatCheckInterval = null;
    this.enabled = true; // Can be disabled if backend is not available
    this.initialConnectionFailed = false;
    
    // Bind methods to maintain context
    this.connect = this.connect.bind(this);
    this.disconnect = this.disconnect.bind(this);
    this.handleMessage = this.handleMessage.bind(this);
    this.reconnect = this.reconnect.bind(this);
    this.handleConnectionFailure = this.handleConnectionFailure.bind(this);
    this.processStream = this.processStream.bind(this);
    this.parseSSEMessage = this.parseSSEMessage.bind(this);
  }

  /**
   * Connect to SSE endpoint using Fetch API (supports custom headers for ngrok)
   */
  connect() {
    // Check if SSE is enabled
    if (!this.enabled) {
      console.log('⚠️ SSE: Service is disabled. Skipping connection.');
      return;
    }

    // Prevent connection in iframes/modals to avoid duplicate connections
    if (window !== window.top) {
      console.log('⚠️ SSE: Skipping connection in iframe/modal to prevent duplicates.');
      return;
    }

    // Prevent multiple connections
    if (this.isConnected || this.reconnectTimeout) {
      console.log('SSE: Already connected or connecting');
      return;
    }

    const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://fnolbackend.alphatorana.com';
    const sseUrl = `${API_BASE_URL}/api/notifications/sse`;

    console.log('🔌 SSE: Connecting to notifications endpoint...', sseUrl);

    // Use async IIFE to avoid making connect() async
    (async () => {
      try {
        // Use fetch instead of EventSource to send custom headers
        const response = await fetch(sseUrl, {
          headers: {
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache',
            'ngrok-skip-browser-warning': 'true', // ← This is the key!
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        // Check content type
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('text/event-stream')) {
          throw new Error(`Invalid content-type: ${contentType}. Expected text/event-stream`);
        }

        console.log('✅ SSE: Connected successfully');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.reconnectDelay = 5000;
        this.lastHeartbeat = Date.now();
        this.initialConnectionFailed = false;
        
        // Start heartbeat monitoring
        this.startHeartbeatMonitoring();
        
        // Notify listeners
        this.notifyListeners('connected', { timestamp: new Date().toISOString() });

        // Process the stream
        await this.processStream(response.body);

      } catch (error) {
        console.error('❌ SSE: Failed to connect:', error.message);
        this.isConnected = false;
        this.stopHeartbeatMonitoring();
        this.notifyListeners('disconnected', { reason: error.message });
        this.handleConnectionFailure();
      }
    })();
  }

  /**
   * Process the SSE stream from fetch response
   */
  async processStream(body) {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (this.isConnected) {
        const { done, value } = await reader.read();
        
        if (done) {
          console.log('🔌 SSE: Stream ended');
          this.isConnected = false;
          this.notifyListeners('disconnected', { reason: 'Stream ended' });
          this.handleConnectionFailure();
          break;
        }

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete messages (separated by \n\n)
        const messages = buffer.split('\n\n');
        buffer = messages.pop() || ''; // Keep incomplete message in buffer

        for (const message of messages) {
          if (message.trim()) {
            this.parseSSEMessage(message);
          }
        }
      }
    } catch (error) {
      console.error('❌ SSE: Stream error:', error);
      this.isConnected = false;
      this.notifyListeners('disconnected', { reason: 'Stream error' });
      this.handleConnectionFailure();
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Parse SSE message format
   */
  parseSSEMessage(message) {
    const lines = message.split('\n');
    let data = '';

    for (const line of lines) {
      if (line.startsWith('data:')) {
        data = line.substring(5).trim();
      }
    }

    if (data) {
      // Create event-like object for handleMessage
      this.handleMessage({ data });
    }
  }

  /**
   * Handle incoming SSE messages
   */
  handleMessage(event) {
    try {
      const data = JSON.parse(event.data);
      
      // Update last heartbeat time
      this.lastHeartbeat = Date.now();

      // Handle different message types
      switch (data.type) {
        case 'heartbeat':
          console.log('💓 SSE: Heartbeat received at', data.timestamp);
          this.notifyListeners('heartbeat', data);
          break;

        case 'new_fnol':
          console.log('🆕 SSE: New FNOL notification received!', data.data);
          this.notifyListeners('newFNOL', data.data);
          
          // Show browser notification if permission granted
          this.showBrowserNotification(data.data);
          break;

        default:
          console.log('📨 SSE: Unknown message type:', data.type);
      }
    } catch (error) {
      console.error('❌ SSE: Failed to parse message:', error);
    }
  }

  /**
   * Handle connection failure with better error messaging
   */
  handleConnectionFailure() {
    // If initial connection fails multiple times, disable SSE
    if (this.reconnectAttempts >= 3 && !this.initialConnectionFailed) {
      this.initialConnectionFailed = true;
      console.warn('⚠️ SSE: Backend notifications service unavailable. Running in polling mode.');
      console.warn('⚠️ SSE: To enable real-time notifications, ensure backend SSE endpoint is running.');
      // Don't disable completely, just stop aggressive reconnection
      this.reconnectDelay = 60000; // Try again after 1 minute
    }
    
    this.reconnect();
  }

  /**
   * Start monitoring heartbeat to detect stale connections
   */
  startHeartbeatMonitoring() {
    // Clear any existing interval
    this.stopHeartbeatMonitoring();
    
    // Check heartbeat every 60 seconds
    this.heartbeatCheckInterval = setInterval(() => {
      const timeSinceLastHeartbeat = Date.now() - this.lastHeartbeat;
      const heartbeatTimeout = 90000; // 90 seconds (3x heartbeat interval)
      
      if (timeSinceLastHeartbeat > heartbeatTimeout) {
        console.warn('⚠️ SSE: No heartbeat received for 90 seconds, reconnecting...');
        this.disconnect();
        this.reconnect();
      }
    }, 60000); // Check every 60 seconds
  }

  /**
   * Stop heartbeat monitoring
   */
  stopHeartbeatMonitoring() {
    if (this.heartbeatCheckInterval) {
      clearInterval(this.heartbeatCheckInterval);
      this.heartbeatCheckInterval = null;
    }
  }

  /**
   * Reconnect with exponential backoff
   */
  reconnect() {
    // Clear any pending reconnect
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    // Check max attempts
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn('⚠️ SSE: Max reconnection attempts reached. Will retry in 5 minutes.');
      console.warn('💡 SSE: The app will continue to work without real-time notifications.');
      console.warn('💡 SSE: Please refresh manually or ensure backend SSE endpoint is running.');
      
      // Schedule one more retry after 5 minutes
      this.reconnectTimeout = setTimeout(() => {
        this.reconnectAttempts = 0; // Reset attempts
        this.reconnectDelay = 5000; // Reset delay
        console.log('🔄 SSE: Retrying connection after extended delay...');
        this.connect();
      }, 300000); // 5 minutes
      
      this.notifyListeners('error', { 
        message: 'SSE notifications unavailable. App continues in polling mode.',
        reconnectAttempts: this.reconnectAttempts 
      });
      return;
    }

    this.reconnectAttempts++;
    
    console.log(`🔄 SSE: Reconnecting in ${this.reconnectDelay / 1000}s (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
      
      // Exponential backoff with max delay
      this.reconnectDelay = Math.min(
        this.reconnectDelay * 1.5,
        this.maxReconnectDelay
      );
    }, this.reconnectDelay);
  }

  /**
   * Disconnect from SSE
   */
  disconnect() {
    console.log('👋 SSE: Disconnecting...');
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.stopHeartbeatMonitoring();

    // Set flag to stop stream processing
    this.isConnected = false;

    this.notifyListeners('disconnected', { reason: 'Manual disconnect' });
  }

  /**
   * Register an event listener
   * @param {string} eventType - Type of event ('newFNOL', 'heartbeat', 'error', 'connected', 'disconnected')
   * @param {Function} callback - Callback function to execute
   * @returns {Function} Unsubscribe function
   */
  on(eventType, callback) {
    if (!this.listeners[eventType]) {
      console.warn(`⚠️ SSE: Unknown event type: ${eventType}`);
      return () => {};
    }

    this.listeners[eventType].push(callback);
    console.log(`📌 SSE: Listener registered for '${eventType}'`);

    // Return unsubscribe function
    return () => {
      this.listeners[eventType] = this.listeners[eventType].filter(cb => cb !== callback);
      console.log(`📌 SSE: Listener unregistered for '${eventType}'`);
    };
  }

  /**
   * Notify all listeners of an event type
   */
  notifyListeners(eventType, data) {
    if (this.listeners[eventType]) {
      this.listeners[eventType].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`❌ SSE: Error in ${eventType} listener:`, error);
        }
      });
    }
  }

  /**
   * Show browser notification for new FNOL
   */
  showBrowserNotification(fnolData) {
    // Check if browser supports notifications
    if (!('Notification' in window)) {
      console.log('📢 Browser does not support notifications');
      return;
    }

    // Check notification permission
    if (Notification.permission === 'granted') {
      this.createNotification(fnolData);
    } else if (Notification.permission !== 'denied') {
      // Request permission
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          this.createNotification(fnolData);
        }
      });
    }
  }

  /**
   * Create and show notification
   */
  createNotification(fnolData) {
    const title = '🆕 New FNOL Received';
    const options = {
      body: `FNOL ID: ${fnolData.fnol_id}\nClaimant: ${fnolData.claimant || 'Unknown'}\nCause: ${fnolData.cause_of_loss}`,
      icon: '/AT_small.png',
      badge: '/AT_small.png',
      tag: fnolData.fnol_id, // Prevent duplicate notifications
      requireInteraction: false, // Auto-dismiss after a few seconds
      silent: false,
      timestamp: new Date(fnolData.created_at).getTime(),
      data: fnolData
    };

    try {
      const notification = new Notification(title, options);
      
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        notification.close();
      };

      // Auto-close after 10 seconds
      setTimeout(() => notification.close(), 10000);
    } catch (error) {
      console.error('❌ Failed to create notification:', error);
    }
  }

  /**
   * Request browser notification permission
   */
  async requestNotificationPermission() {
    if (!('Notification' in window)) {
      console.log('📢 Browser does not support notifications');
      return 'unsupported';
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission === 'denied') {
      return 'denied';
    }

    const permission = await Notification.requestPermission();
    return permission;
  }

  /**
   * Get current connection status
   */
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      lastHeartbeat: this.lastHeartbeat ? new Date(this.lastHeartbeat).toISOString() : null,
      readyState: this.isConnected ? 'OPEN' : 'CLOSED',
      enabled: this.enabled,
      initialConnectionFailed: this.initialConnectionFailed
    };
  }

  /**
   * Enable or disable SSE service
   */
  setEnabled(enabled) {
    this.enabled = enabled;
    if (!enabled) {
      console.log('⚠️ SSE: Service disabled by user');
      this.disconnect();
    } else {
      console.log('✅ SSE: Service enabled by user');
      this.reconnectAttempts = 0;
      this.reconnectDelay = 5000;
      this.initialConnectionFailed = false;
      this.connect();
    }
  }
}

// Create singleton instance
const sseService = new SSEService();

export default sseService;
